import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@daater/api';
import { getCurrentAccountFromApiError, shouldRetryAccountQuery } from './current-account-access';
import { isSameRecordsSearchIgnoringColumns } from './records-search';
import {
  changePassword,
  confirmPaymentFromPayu,
  createPaymentOrder,
  deleteSavedReport,
  fetchAnalyticsHistoric,
  fetchCurrentAccount,
  fetchCurrentTeam,
  fetchDataStructure,
  fetchPaymentHistory,
  fetchSavedReports,
  fetchTableAnalytics,
  fetchTableFields,
  fetchTableFilters,
  searchTableRecords,
  loginRequest,
  registerUserRequest,
  requestPasswordRecovery,
  resetPassword,
  switchAccount,
  updateLastTable,
  updateTerms,
  updateUserTableSettings,
} from './endpoints';
import type {
  AnalyticsRequest,
  RegisterPayload,
  SearchRecordsBody,
  UpdateTableSettingsPayload,
} from './types';
import { useAccountStore, type Account } from '../lib/account-store';
import { useAuthStore } from '../lib/auth-store';

export const queryKeys = {
  account: (token?: string | null) => ['account', token],
  currentTeam: ['current-team'],
  dataStructure: ['data-structure'],
  analyticsHistoric: ['analytics-historic'],
  tableRecords: (tableId: string | undefined, body: SearchRecordsBody) =>
    ['table-records', tableId, body] as const,
  tableFields: (tableId?: string) => ['table-fields', tableId],
  tableFilters: (tableId?: string) => ['table-filters', tableId],
  savedReports: (tableId?: string) => ['saved-reports', tableId],
  paymentHistory: (page = 1) => ['payment-history', page],
  tableAnalytics: (tableId: string | undefined, body: AnalyticsRequest | null) =>
    ['table-analytics', tableId, body] as const,
};

export const useLoginMutation = () => {
  return useMutation({
    mutationFn: loginRequest,
  });
};

export const usePasswordRecoveryMutation = () => {
  return useMutation({
    mutationFn: requestPasswordRecovery,
  });
};

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: resetPassword,
  });
};

export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: changePassword,
  });
};

export const useUpdateTermsMutation = () => {
  return useMutation({
    mutationFn: updateTerms,
  });
};

export const useCurrentAccountQuery = (token?: string | null) => {
  const setAccount = useAccountStore(state => state.setAccount);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const enabled = Boolean(token);

  const query = useQuery({
    queryKey: queryKeys.account(token),
    queryFn: fetchCurrentAccount,
    enabled,
    retry: shouldRetryAccountQuery,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (query.data) {
      setAccount(query.data);
    }
  }, [query.data, setAccount]);

  useEffect(() => {
    if (query.error) {
      const accountFromError = getCurrentAccountFromApiError(query.error);
      if (accountFromError) {
        setAccount(accountFromError);
      }

      if (query.error instanceof ApiError && query.error.status === 401) {
        clearAuth();
      }
    }
  }, [query.error, clearAuth, setAccount]);

  return query;
};

export const useCurrentTeamQuery = (userId?: string) => {
  return useQuery({
    queryKey: queryKeys.currentTeam,
    queryFn: () => fetchCurrentTeam(userId as string),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
};

export const useDataStructureQuery = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.dataStructure,
    queryFn: fetchDataStructure,
    enabled,
    staleTime: 60_000,
  });
};

export const useAnalyticsHistoricQuery = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.analyticsHistoric,
    queryFn: fetchAnalyticsHistoric,
    enabled,
    staleTime: 30_000,
  });
};

export const useTableRecordsQuery = (tableId: string | undefined, body: SearchRecordsBody) => {
  return useQuery({
    queryKey: queryKeys.tableRecords(tableId, body),
    queryFn: () => searchTableRecords(tableId as string, body),
    enabled: Boolean(tableId) && body.filters.length > 0 && body.columns.length > 0,
    placeholderData: (previousData, previousQuery) =>
      isSameRecordsSearchIgnoringColumns(previousQuery?.queryKey, tableId, body)
        ? previousData
        : undefined,
  });
};

export const useTableFieldsQuery = (tableId?: string) => {
  return useQuery({
    queryKey: queryKeys.tableFields(tableId),
    queryFn: () => fetchTableFields(tableId as string),
    enabled: Boolean(tableId),
  });
};

export const useTableFiltersQuery = (tableId?: string) => {
  return useQuery({
    queryKey: queryKeys.tableFilters(tableId),
    queryFn: () => fetchTableFilters(tableId as string),
    enabled: Boolean(tableId),
  });
};

export const useSavedReportsQuery = (tableId?: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.savedReports(tableId),
    queryFn: () => fetchSavedReports(tableId as string),
    enabled: Boolean(tableId) && enabled,
  });
};

export const useSwitchAccountMutation = () => {
  const queryClient = useQueryClient();
  const account = useAccountStore(state => state.account);
  const setAccount = useAccountStore(state => state.setAccount);

  return useMutation({
    mutationFn: switchAccount,
    onSuccess: (_data, variables) => {
      const nextAccount: Account = {
        ...(account ?? {}),
        _id: variables.account,
        dataset: variables.dataset,
        table: variables.table,
      };
      setAccount(nextAccount);
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      void queryClient.invalidateQueries({ queryKey: ['table-records'] });
    },
  });
};

export const useUpdateSettingsMutation = () => {
  return useMutation({
    mutationFn: (payload: UpdateTableSettingsPayload) => updateUserTableSettings(payload),
  });
};

export const useDeleteSavedReportMutation = (tableId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => deleteSavedReport(tableId as string, reportId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.savedReports(tableId) });
    },
  });
};

export const useCreatePaymentOrderMutation = () => {
  return useMutation({
    mutationFn: createPaymentOrder,
  });
};

export const useConfirmPaymentFromPayuMutation = () => {
  return useMutation({
    mutationFn: confirmPaymentFromPayu,
  });
};

export const usePaymentHistoryQuery = (page = 1) => {
  return useQuery({
    queryKey: queryKeys.paymentHistory(page),
    queryFn: () => fetchPaymentHistory(page),
  });
};

export const useRegisterUserMutation = () => {
  return useMutation({
    mutationFn: (payload: RegisterPayload) => registerUserRequest(payload),
  });
};

export const useUpdateLastTableMutation = () => {
  return useMutation({
    mutationFn: (tableId: string) => updateLastTable(tableId),
  });
};

export const useTableAnalyticsQuery = (
  tableId: string | undefined,
  body: AnalyticsRequest | null
) => {
  return useQuery({
    queryKey: queryKeys.tableAnalytics(tableId, body),
    queryFn: () => fetchTableAnalytics(tableId as string, body as AnalyticsRequest),
    enabled: Boolean(tableId) && body !== null,
  });
};
